import type { HarmonizedViolationGovStatus } from "encamp-prisma/generated/browser";

import { DateTime } from "luxon";

/**
 * Display labels for the user-tracked resolution workflow. Explicit rather
 * than prettyPrintEnumValue: two labels carry a parenthetical qualifier and
 * mixed casing that no generic enum formatter can produce. Shared so the export
 * prints the same words the grid does.
 */
export const GOV_STATUS_LABEL = {
  UNDER_ASSESSMENT: "Under Assessment",
  RESOLUTION_IN_PROGRESS_CUSTOMER_REVIEWING: "Resolution In Progress (Customer reviewing)",
  RESOLUTION_IN_PROGRESS_ENCAMP_EXECUTING: "Resolution In Progress (Encamp executing)",
  AGENCY_REVIEW: "Agency Review",
  RESOLVED: "Resolved",
  CLOSED_WONT_DO: "Closed - No Action Needed",
} satisfies Record<HarmonizedViolationGovStatus, string>;

/**
 * Oak reads ISO strings off GraphQL and the export worker reads `Date`s off
 * Prisma, so the date helpers take either and the two need different zones. A
 * `Date` is a `@db.Date` column, which is UTC midnight: read in the process's
 * own zone it reports the previous calendar day west of UTC.
 */
function toDateTime(value: Date | string | null | undefined): DateTime | null {
  if (!value) return null;
  if (value instanceof Date) {
    const fromDate = DateTime.fromJSDate(value, { zone: "utc" });
    return fromDate.isValid ? fromDate : null;
  }
  // GraphQL hands the browser an ISO string, rendered in the viewer's own zone.
  // ast-grep-ignore: no-zoneless-luxon-parse-ts
  const fromIso = DateTime.fromISO(value);
  return fromIso.isValid ? fromIso : null;
}

/**
 * What a cell with no value reads as. The grid's em dash is noise in a
 * spreadsheet, where it breaks sorting and filtering on the column.
 */
export const EMPTY_GRID_CELL = "—";
export const EMPTY_EXPORT_CELL = "";

export function formatDiscoveryQuarter(
  value: Date | string | null | undefined,
  emptyValue: string = EMPTY_GRID_CELL
): string {
  const dt = toDateTime(value);
  if (!dt) return emptyValue;
  const quarter = Math.floor((dt.month - 1) / 3) + 1;
  return `${dt.year} Q${quarter}`;
}

export function formatDaysToResolution(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
  emptyValue: string = EMPTY_GRID_CELL
): string {
  const s = toDateTime(start);
  const e = toDateTime(end);
  if (!s || !e) return emptyValue;
  return String(Math.round(e.diff(s, "days").days));
}
